import { getTodayString, getLocalISOString } from '../utils/dateUtils';
import { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { 
  ViewLoadDialog,
  LoadCard,
  EmptyLoadsState,
  DraftsSection,
  FilterDialog,
  PaginationControls,
  LoadingSkeleton,
  LoadsHeader,
  DeleteConfirmDialog,
  DraftSavedDialog,
  AddEditLoadDialog
} from '../components/silica-loads';
import { AddEditAdhocLoadDialog } from '../components/silica-loads/adhoc/AddEditAdhocLoadDialog';
import { AdhocLoadsManagerDialog } from '../components/silica-loads/adhoc/AdhocLoadsManagerDialog';
import { exportToCSV, SILICA_LOAD_COLUMNS } from '../utils/csvExport';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '');
const API = `${BACKEND_URL}/api`;

// Supported file types for R2 upload
const SUPPORTED_FILE_TYPES = ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.tiff', '.tif', '.pdf', '.txt', '.docx', '.csv', '.xlsx', '.xls'];

export default function SilicaLoads() {
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingLoad, setEditingLoad] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [activeTab, setActiveTab] = useState('loads');
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, type: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewingLoad, setViewingLoad] = useState(null);
  const [attachmentLoading, setAttachmentLoading] = useState({});
  const [editSection, setEditSection] = useState(null); // Track which section to edit
  const [returnToView, setReturnToView] = useState(false); // Track if we should return to view dialog on cancel
  const [adminOverride, setAdminOverride] = useState(false); // Super-user "Master Edit" bypass
  // Adhoc load dialog (isolated flow — triggered when user picks "Adhoc Client" in the client dropdown)
  const [showAdhocDialog, setShowAdhocDialog] = useState(false);
  const [editingAdhocLoad, setEditingAdhocLoad] = useState(null); // when set, dialog opens in EDIT mode
  const [adhocAdminOverride, setAdhocAdminOverride] = useState(false); // Master Edit bypass for adhoc
  // Adhoc loads manager (update-weight / adjustments / summary invoice — Phase 2b flows)
  const [showAdhocManager, setShowAdhocManager] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLoads, setTotalLoads] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalTons, setTotalTons] = useState(0);
  const [breakdown, setBreakdown] = useState([]);
  const pageSize = 20;
  // Filter state
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', clientName: '', acceptanceStatus: '', billStatus: '' });
  const [activeFilters, setActiveFilters] = useState({ dateFrom: '', dateTo: '', clientName: '', acceptanceStatus: '', billStatus: '' });
  const [category, setCategory] = useState('all');
  // Search state - searchInput for display, searchQuery for API (debounced)
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounceTimer, setSearchDebounceTimer] = useState(null);
  // Draft saved dialog state
  const [showDraftSavedDialog, setShowDraftSavedDialog] = useState(false);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isApprover = currentUser.role === 'approver' || currentUser.role === 'super_approver';
  const isSuperUser = currentUser.role === 'super_approver';
  const canDelete = currentUser.role === 'super_approver';

  // Load drafts from localStorage
  useEffect(() => {
    const savedDrafts = localStorage.getItem('silica_load_drafts');
    if (savedDrafts) {
      setDrafts(JSON.parse(savedDrafts));
    }
  }, []);

  // Listen for "open adhoc load dialog" events fired from the client dropdown
  // inside AddEditLoadDialog when the user picks "Adhoc Client".
  useEffect(() => {
    const handler = () => {
      setShowDialog(false);       // close any open standard dialog first
      setShowAdhocDialog(true);   // open the isolated adhoc dialog
    };
    window.addEventListener('open-adhoc-load-dialog', handler);
    return () => window.removeEventListener('open-adhoc-load-dialog', handler);
  }, []);

  // Save draft to localStorage
  const saveDraft = (draftData, showNotification = true) => {
    const draftId = `draft_${Date.now()}`;
    const newDraft = {
      ...draftData,
      draft_id: draftId,
      draft_date: getLocalISOString(),
    };
    const updatedDrafts = [...drafts, newDraft];
    setDrafts(updatedDrafts);
    localStorage.setItem('silica_load_drafts', JSON.stringify(updatedDrafts));
    if (showNotification) {
      toast.success('Draft saved successfully!');
    }
  };

  // Handle dialog close - receives draft data from child if unsaved
  const handleDialogClose = (draftData) => {
    if (draftData) {
      const isExplicit = draftData.explicit_save;
      delete draftData.explicit_save;
      saveDraft(draftData, isExplicit);
      if (!isExplicit) setShowDraftSavedDialog(true);
    }
    setShowDialog(false);
    setEditingLoad(null);
    setAdminOverride(false);
    
    // If we were editing a section from view mode, return to view dialog
    if (returnToView && viewingLoad) {
      setShowViewDialog(true);
      setReturnToView(false);
    }
    setEditSection(null);
  };

  // Load draft into form
  const loadDraft = (draft) => {
    // Remove draft from list
    const updatedDrafts = drafts.filter(d => d.draft_id !== draft.draft_id);
    setDrafts(updatedDrafts);
    localStorage.setItem('silica_load_drafts', JSON.stringify(updatedDrafts));
    setActiveTab('loads');
    // Pass draft as editingLoad (without load_id, dialog treats it as "add")
    setEditingLoad({ ...draft });
    setShowDialog(true);
    toast.info('Draft loaded. Complete and submit the form.');
  };

  // Delete draft
  const deleteDraft = (draftId) => {
    const updatedDrafts = drafts.filter(d => d.draft_id !== draftId);
    setDrafts(updatedDrafts);
    localStorage.setItem('silica_load_drafts', JSON.stringify(updatedDrafts));
    toast.success('Draft deleted');
  };

  useEffect(() => { fetchLoads(); }, [currentPage, activeFilters, category, searchQuery]);

  const fetchLoads = async (isRetry = false) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', pageSize);
      if (activeFilters.dateFrom) params.append('date_from', activeFilters.dateFrom);
      if (activeFilters.dateTo) params.append('date_to', activeFilters.dateTo);
      if (activeFilters.clientName) params.append('client_name', activeFilters.clientName);
      if (activeFilters.acceptanceStatus) params.append('acceptance_status', activeFilters.acceptanceStatus);
      if (activeFilters.billStatus) params.append('bill_status', activeFilters.billStatus);
      if (category && category !== 'all') params.append('category', category);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      
      const response = await axios.get(`${API}/silica-loads?${params.toString()}`, { 
        headers: { Authorization: `Bearer ${token}` },
        timeout: 20000
      });
      
      if (response.data.items) {
        setLoads(response.data.items);
        setTotalPages(response.data.total_pages || 1);
        setTotalLoads(response.data.total || response.data.items.length);
        setTotalAmount(response.data.total_amount || 0);
        setTotalTons(response.data.total_tons || 0);
        setBreakdown(response.data.breakdown || []);
      } else {
        setLoads(response.data);
        setTotalPages(Math.ceil(response.data.length / pageSize));
        setTotalLoads(response.data.length);
        setTotalAmount(0);
        setTotalTons(0);
        setBreakdown([]);
      }
    } catch (error) {
      console.error('Silica loads fetch error:', error);
      if (!isRetry && (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED')) {
        setTimeout(() => fetchLoads(true), 2000);
        return;
      }
      if (error.code === 'ERR_NETWORK') {
        toast.error('Network error - check your connection');
      } else if (error.response?.status >= 500) {
        toast.error('Server temporarily unavailable');
      } else {
        toast.error('Failed to load silica loads');
      }
    } 
    finally { setLoading(false); }
  };

  // Apply filters
  const applyFilters = () => {
    setActiveFilters({ ...filters });
    setCurrentPage(1);
    setShowFilterDialog(false);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({ dateFrom: '', dateTo: '', clientName: '', acceptanceStatus: '', billStatus: '' });
    setActiveFilters({ dateFrom: '', dateTo: '', clientName: '', acceptanceStatus: '', billStatus: '' });
    setCurrentPage(1);
    setShowFilterDialog(false);
  };

  const handleDelete = (loadId) => {
    setDeleteConfirm({ open: true, id: loadId, type: 'load' });
  };
  
  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/silica-loads/${deleteConfirm.id}`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success('Silica load deleted successfully');
      fetchLoads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete silica load');
    } finally {
      setDeleteLoading(false);
      setDeleteConfirm({ open: false, id: null, type: '' });
    }
  };
  
  const openAddDialog = () => {
    setEditingLoad(null);
    setShowDialog(true);
  };
  
  const openEditDialog = (load, section = null) => {
    // Adhoc loads use the isolated adhoc edit dialog
    if (load?.client_type === 'adhoc') {
      setEditingAdhocLoad(load);
      setAdhocAdminOverride(false);
      setShowAdhocDialog(true);
      return;
    }
    setEditingLoad(load);
    setAdminOverride(false);
    setShowDialog(true);
    // Store section for AddEditLoadDialog to know which section to focus/expand
    if (section) {
      setEditSection(section);
    } else {
      setEditSection(null);
    }
  };

  const openAdminEditDialog = (load) => {
    // Adhoc loads use the isolated adhoc edit dialog (with locks bypassed too)
    if (load?.client_type === 'adhoc') {
      setEditingAdhocLoad(load);
      setAdhocAdminOverride(true);
      setShowAdhocDialog(true);
      return;
    }
    setEditingLoad(load);
    setAdminOverride(true);      // Bypass ALL section locks
    setEditSection(null);
    setShowDialog(true);
  };

  const handleSaved = () => {
    setShowDialog(false);
    setEditingLoad(null);
    setEditSection(null);
    setAdminOverride(false);
    setReturnToView(false); // Don't return to view after successful save
    fetchLoads();
  };

  const downloadFile = async (loadId, fileType, index = 0, filename = 'file') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/silica-loads/${loadId}/file/${fileType}?index=${index}`, { 
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create blob URL and trigger download
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename || 'file';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      
      toast.success('File downloaded');
    } catch (error) { 
      console.error('Download error:', error);
      if (error.response?.status === 404) {
        toast.error('File not found');
      } else {
        toast.error('Failed to download'); 
      }
    }
  };

  // R2 Cloud Storage Functions
  const getR2Attachment = async (loadId, attachType, displayName, index = 0) => {
    setAttachmentLoading(prev => ({ ...prev, [`${loadId}_${attachType}`]: true }));
    try {
      const token = localStorage.getItem('token');
      // Use the new file endpoint that handles both R2 and legacy base64
      const url = attachType === 'other' 
        ? `${API}/silica-loads/${loadId}/file/${attachType}?index=${index}`
        : `${API}/silica-loads/${loadId}/file/${attachType}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Determine content type from response or filename
      const contentType = response.headers['content-type'] || 'application/octet-stream';
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a download link and trigger it - works better on mobile
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = displayName || `${attachType}_${loadId}`;
      
      // Always download files directly to ensure correct filename is used
      // Opening in new tab shows blob URL instead of our custom filename
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
      toast.success('File opened successfully');
    } catch (error) {
      console.error('Get attachment error:', error);
      if (error.response?.status === 404) {
        toast.error('No file found');
      } else {
        toast.error('Failed to retrieve file');
      }
    } finally {
      setAttachmentLoading(prev => ({ ...prev, [`${loadId}_${attachType}`]: false }));
    }
  };

  const uploadR2Attachment = async (loadId, attachType, files) => {
    if (!files || files.length === 0) return;
    const invalidFiles = Array.from(files).filter(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      return !SUPPORTED_FILE_TYPES.includes(ext);
    });
    if (invalidFiles.length > 0) {
      toast.error(`Unsupported file type(s): ${invalidFiles.map(f => f.name).join(', ')}`);
      return;
    }
    setAttachmentLoading(prev => ({ ...prev, [`${loadId}_${attachType}`]: true }));
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      const response = await axios.post(`${API}/silica-loads/${loadId}/attachment/${attachType}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`Uploaded ${response.data.file_count} file(s) successfully`);
      fetchLoads();
    } catch (error) {
      console.error('Upload error:', error);
      const message = error.response?.data?.detail || 'Failed to upload attachment';
      toast.error(message);
    } finally {
      setAttachmentLoading(prev => ({ ...prev, [`${loadId}_${attachType}`]: false }));
    }
  };

  const deleteR2Attachment = async (loadId, attachType) => {
    setAttachmentLoading(prev => ({ ...prev, [`${loadId}_${attachType}`]: true }));
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/silica-loads/${loadId}/attachment/${attachType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Attachment deleted');
      fetchLoads();
    } catch (error) {
      toast.error('Failed to delete attachment');
    } finally {
      setAttachmentLoading(prev => ({ ...prev, [`${loadId}_${attachType}`]: false }));
    }
  };

  if (loading) return (
    <Layout>
      <LoadingSkeleton />
    </Layout>
  );

  return (
    <Layout>
      <div className="space-y-6">
        <LoadsHeader
          isApprover={isApprover}
          draftsCount={drafts.length}
          activeTab={activeTab}
          totalLoads={totalLoads}
          onToggleDrafts={() => setActiveTab(activeTab === 'drafts' ? 'loads' : 'drafts')}
          onOpenFilter={() => setShowFilterDialog(true)}
          onClearFilter={clearFilters}
          onOpenAdd={openAddDialog}
          onOpenAdhocManager={() => setShowAdhocManager(true)}
          hasActiveFilters={!!(activeFilters.dateFrom || activeFilters.dateTo || activeFilters.clientName || activeFilters.acceptanceStatus || activeFilters.billStatus)}
        />

        {/* Drafts Section */}
        {activeTab === 'drafts' && (
          <DraftsSection 
            drafts={drafts}
            onLoadDraft={loadDraft}
            onDeleteDraft={deleteDraft}
            onBack={() => setActiveTab('loads')}
          />
        )}

        {/* Add/Edit Load Dialog */}
        <AddEditLoadDialog
          key={`${editingLoad?.load_id || 'new'}-${editSection || 'full'}-${adminOverride ? 'admin' : 'std'}`}
          open={showDialog}
          editingLoad={editingLoad}
          isApprover={isApprover}
          onSaved={handleSaved}
          onClose={handleDialogClose}
          initialSection={editSection}
          adminOverride={adminOverride}
        />

        {/* Adhoc Load Dialog — isolated flow for one-off / temporary clients (create + edit) */}
        <AddEditAdhocLoadDialog
          // Use a stable session key so a mid-flight promote from create →
          // edit (after "Generate Invoice") does NOT remount the dialog and
          // throw away the user's typed fields (driver, trip details,
          // delivery info, etc.). Internal useEffect re-syncs on load_id
          // change without a full unmount.
          key={`adhoc-${adhocAdminOverride ? 'admin' : 'std'}`}
          open={showAdhocDialog}
          editingLoad={editingAdhocLoad}
          adminOverride={adhocAdminOverride}
          onClose={() => {
            setShowAdhocDialog(false);
            setEditingAdhocLoad(null);
            setAdhocAdminOverride(false);
          }}
          onSaved={() => {
            setShowAdhocDialog(false);
            setEditingAdhocLoad(null);
            setAdhocAdminOverride(false);
            fetchLoads(); // refresh the list so the new/edited adhoc load appears
          }}
          onRefresh={(updated) => {
            // File upload/delete happened — keep dialog open, but refresh
            // the list underneath AND the editingAdhocLoad snapshot so
            // downstream sections see the new filenames immediately.
            setEditingAdhocLoad(updated);
            fetchLoads();
          }}
        />

        {/* Adhoc Loads Manager — Phase 2b: update-weight / adjustments / summary invoice */}
        <AdhocLoadsManagerDialog
          open={showAdhocManager}
          onClose={() => {
            setShowAdhocManager(false);
            fetchLoads(); // refresh the list; adjustments may change totals
          }}
        />

        {/* Category Tabs */}
        {activeTab === 'loads' && (
          <div className="flex gap-2 overflow-x-auto pb-1 -mt-2" data-testid="category-tabs">
            {[
              { key: 'all', label: 'All' },
              { key: 'delivery', label: 'Delivery' },
              { key: 'bill_pending', label: 'Bill Pending' },
              { key: 'partially_paid', label: 'Partially Paid' },
              { key: 'partially_paid_overdue', label: 'Partially Paid Past Due' },
              { key: 'overdue', label: 'Past Due' },
              { key: 'paid', label: 'Paid' },
            ].map((tab) => (
              <button
                key={tab.key}
                data-testid={`category-tab-${tab.key}`}
                onClick={() => { setCategory(tab.key); setCurrentPage(1); }}
                className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  category === tab.key
                    ? 'bg-slate-900 text-white'
                    : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-400'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {/* Invoice Search */}
        {activeTab === 'loads' && (
          <div className="relative" data-testid="invoice-search-box">
            <input
              type="text"
              placeholder="Search by invoice number..."
              value={searchInput}
              onChange={(e) => {
                const val = e.target.value;
                setSearchInput(val);
                if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
                const timer = setTimeout(() => {
                  setSearchQuery(val);
                  setCurrentPage(1);
                }, 300);
                setSearchDebounceTimer(timer);
              }}
              className="w-full h-10 pl-10 pr-10 rounded-xl border border-slate-200 bg-white text-sm placeholder:text-slate-400 focus:outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-400 transition-colors"
              data-testid="invoice-search-input"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><circle cx="11" cy="11" r="8"/><path strokeLinecap="round" d="m21 21-4.35-4.35"/></svg>
            {searchInput && (
              <button
                onClick={() => { 
                  setSearchInput('');
                  setSearchQuery(''); 
                  setCurrentPage(1);
                  if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="invoice-search-clear"
              >
                <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" d="M6 18 18 6M6 6l12 12"/></svg>
              </button>
            )}
          </div>
        )}

        {/* Load Cards - Show only when on loads tab */}
        {activeTab === 'loads' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50/50 p-4 rounded-2xl">
          {loads.length === 0 ? (
            <EmptyLoadsState />
          ) : loads.map((load) => (
            <LoadCard
              key={load.load_id}
              load={load}
              isApprover={isApprover}
              isSuperUser={isSuperUser}
              canDelete={canDelete}
              onView={(l) => { setViewingLoad(l); setShowViewDialog(true); }}
              onEdit={openEditDialog}
              onAdminEdit={openAdminEditDialog}
              onDelete={handleDelete}
              onDownloadFile={downloadFile}
            />
          ))}

          {/* Total Amount + Client-wise Breakdown */}
          {loads.length > 0 && (
            <div
              className="sm:col-span-2 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl shadow-sm overflow-hidden"
              data-testid="silica-loads-total-bar"
            >
              {/* Header row: tab label + grand total + total tons */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-amber-200 bg-white/40 gap-3">
                <span className="text-xs uppercase tracking-wide font-semibold text-amber-800">
                  {({
                    all: 'All Loads',
                    delivery: 'Delivery',
                    bill_pending: 'Bill Pending',
                    partially_paid: 'Partially Paid',
                    partially_paid_overdue: 'Partially Paid Past Due',
                    overdue: 'Past Due',
                    paid: 'Paid',
                  })[category] || 'Total'} · {totalLoads} {totalLoads === 1 ? 'load' : 'loads'}
                </span>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Total Tons</div>
                    <div
                      className="text-sm sm:text-base font-bold text-amber-900 whitespace-nowrap"
                      data-testid="silica-loads-total-tons"
                    >
                      {Number(totalTons || 0).toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} T
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Total Amount</div>
                    <div
                      className="text-lg font-extrabold text-amber-900"
                      data-testid="silica-loads-total-amount"
                    >
                      ₹{Number(totalAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Client-wise breakdown */}
              {breakdown.length > 0 && (
                <div className="divide-y divide-amber-100" data-testid="silica-loads-breakdown">
                  {breakdown.map((b) => {
                    const short = b.client_name === 'SVE CASTINGS PVT LTD' ? 'SVE'
                      : b.client_name === 'BALAJI CASTINGS PVT LTD' ? 'BALAJI'
                      : b.client_name;
                    const badgeColor = short === 'SVE'
                      ? 'bg-blue-100 text-blue-700'
                      : short === 'BALAJI'
                      ? 'bg-purple-100 text-purple-700'
                      : 'bg-slate-100 text-slate-700';
                    return (
                      <div
                        key={b.client_name}
                        className="px-3 sm:px-5 py-2.5"
                        data-testid={`breakdown-row-${short.toLowerCase()}`}
                      >
                        <div className="flex items-center gap-2 mb-2 sm:mb-0 sm:hidden">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${badgeColor}`}>{short}</span>
                          <span className="text-xs text-slate-500">{b.count} {b.count === 1 ? 'load' : 'loads'}</span>
                        </div>
                        <div className="sm:flex sm:items-center sm:gap-3">
                          <div className="hidden sm:flex sm:items-center gap-2 min-w-[140px]">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${badgeColor}`}>{short}</span>
                            <span className="text-xs text-slate-500">{b.count} {b.count === 1 ? 'load' : 'loads'}</span>
                          </div>
                          <div className="flex-1 grid grid-cols-4 gap-2 sm:gap-3 text-center sm:text-right">
                            <div>
                              <div className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold">Tons</div>
                              <div
                                className="text-xs sm:text-sm font-semibold text-amber-800 whitespace-nowrap"
                                data-testid={`breakdown-tons-${short.toLowerCase()}`}
                              >
                                {Number(b.tons || 0).toLocaleString('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} T
                              </div>
                            </div>
                            <div>
                              <div className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold">Invoice</div>
                              <div className="text-xs sm:text-sm font-bold text-slate-800 whitespace-nowrap">₹{Number(b.invoice_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                            </div>
                            <div>
                              <div className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold">Transport</div>
                              <div className="text-xs sm:text-sm font-semibold text-rose-700 whitespace-nowrap">₹{Number(b.transport || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                            </div>
                            <div>
                              <div className="text-[9px] uppercase tracking-wide text-slate-500 font-semibold">Profit</div>
                              <div className="text-xs sm:text-sm font-bold text-emerald-700 whitespace-nowrap">₹{Number(b.profit || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Pagination Controls */}
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={totalLoads}
            pageSize={pageSize}
          />
        </div>
        )}
      </div>
      
      {/* Filter Dialog */}
      <FilterDialog
        open={showFilterDialog}
        onOpenChange={setShowFilterDialog}
        filters={filters}
        setFilters={setFilters}
        onApply={applyFilters}
        onClear={clearFilters}
      />
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => !open && setDeleteConfirm({ open: false, id: null, type: '' })}
        type={deleteConfirm.type}
        onConfirm={confirmDelete}
        loading={deleteLoading}
      />

      {/* Draft Saved Dialog */}
      <DraftSavedDialog
        open={showDraftSavedDialog}
        onOpenChange={setShowDraftSavedDialog}
        onViewDrafts={() => {
          setShowDraftSavedDialog(false);
          setActiveTab('drafts');
        }}
        onDiscard={() => {
          if (drafts.length > 0) {
            const updatedDrafts = drafts.slice(0, -1);
            setDrafts(updatedDrafts);
            localStorage.setItem('silica_load_drafts', JSON.stringify(updatedDrafts));
            toast.info('Draft discarded');
          }
          setShowDraftSavedDialog(false);
        }}
      />

      {/* View Load Dialog */}
      <ViewLoadDialog
        open={showViewDialog}
        onClose={setShowViewDialog}
        load={viewingLoad}
        onEdit={openEditDialog}
        onEditSection={(load, section) => {
          // Close view dialog and open edit dialog with the specific section
          setShowViewDialog(false);
          setReturnToView(true); // Mark that we should return to view on cancel
          openEditDialog(load, section);
        }}
        onGetR2File={getR2Attachment}
        attachmentLoading={attachmentLoading}
        isSuperUser={isSuperUser}
      />
    </Layout>
  );
}
